import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import { PARTNER_OFFERINGS } from "../../data/partnerOfferings";
import "./MotilalOswalPage.css";

const MOTILAL_ACCOUNT_OPEN_URL = "https://mosl.co/MOSWEB/pHhFEUMLky";

const MotilalOswalPage = () => {
  const navigate = useNavigate();
  const partner = PARTNER_OFFERINGS.motilal;
  const [openSections, setOpenSections] = useState([0, 1]);
  const [openFaq, setOpenFaq] = useState(0);
  const [activeProduct, setActiveProduct] = useState(null);

  const FAQS = [
    {
      q: "What services are offered through your partnership with Motilal Oswal?",
      a: "Through our partnership with Motilal Oswal, we offer market access (including STRATX for automated F&O), mutual funds, PMS, AIF Navigator and AIF strategies, fixed income, insurance, unlisted opportunities, and lending-led wealth solutions.",
    },
    {
      q: "How does the partnership with Motilal Oswal benefit clients?",
      a: "Clients get access to a research-backed, one-platform ecosystem with broad product depth and pan-India service coverage supported by advisory-led execution.",
    },
    {
      q: "What types of investment options are available?",
      a: "Options include equities and derivatives (including STRATX for algo-style F&O), mutual funds, PMS, AIF Navigator suite and Category II/III AIFs, bonds, fixed deposits, insurance, and select unlisted opportunities.",
    },
    {
      q: "Do you provide investment advisory services?",
      a: "Yes. We provide research-backed advisory with portfolio monitoring, review, and restructuring aligned to your goals, risk profile, and market conditions.",
    },
    {
      q: "Is there support for beginners in investing?",
      a: "Yes. We help with onboarding, KYC, asset-allocation guidance, and suitable starting options such as SIP-based investing and diversified product selection.",
    },
    {
      q: "What trading platforms are available to clients?",
      a: "Clients can access digital trading and investment tools for execution, portfolio visibility, and consolidated tracking across products from a single interface.",
    },
    {
      q: "How do I open an account?",
      a: "Account opening is completed through a simple KYC-compliant workflow with assisted support for documentation, activation, and first investment setup.",
    },
    {
      q: "Are there any minimum investment requirements?",
      a: "Minimums vary by product. Mutual funds can start small via SIPs, while PMS and several AIF strategies have higher minimum commitment requirements.",
    },
    {
      q: "What kind of research and insights are provided?",
      a: "Clients receive house views, model portfolios, market and sector updates, strategy insights, and product-level guidance from Motilal Oswal research.",
    },
    {
      q: "Do you provide ongoing portfolio monitoring?",
      a: "Yes. We provide ongoing portfolio monitoring, periodic reviews, and realignment recommendations based on changing market and goal requirements.",
    },
    {
      q: "How secure are the transactions and investments?",
      a: "Transactions are routed through regulated and secure channels with standard compliance safeguards, platform authentication, and data protection controls.",
    },
    {
      q: "How can clients get support or resolve queries?",
      a: "Clients can contact our advisory team for investment guidance.",
    },
  ];

  const IMP_INFO_HUB_BASE =
    "https://onlinetrade.motilaloswal.com/emailers/Online_Products/IMP/infohub";
  const IMP_INFO_HUB_INDEX = `${IMP_INFO_HUB_BASE}/index.html`;

  const buildImpInfoHubProductDetails = () => {
    const leafletUrl = (pdfPath) => encodeURI(`${IMP_INFO_HUB_BASE}${pdfPath}`);

    const strip = (pdfPath) => ({
      description:
        "Motilal Oswal Intelligent Model Portfolio (IMP) collateral from the official IMP Info Hub — multilingual materials, quick download and share, with on-the-go access to model portfolio leaflets and overview decks.",
      features: [
        "Research-led model portfolio frameworks published on Motilal's IMP Info Hub",
        `Hub index: ${IMP_INFO_HUB_INDEX}`,
        `Direct leaflet / deck: ${leafletUrl(pdfPath)}`,
        "Use with our advisory team for suitability, allocation fit, and execution support",
      ],
    });

    const entries = [
      ["IMP Product Guide", "/img/overview-deak/Intelligent-Model-Portfolio.pdf"],
      ["IMP Product Brochure", "/pdf/MO_IMP Brochure_revamp_V2_high-res.pdf"],
      ["MO Signature Model Portfolio", "/pdf/MO-IMP-Signature-Leaflet.pdf"],
      ["AlphaNext Model Portfolio", "/pdf/MO-IMP-Leaflet-Alphanext-Model.pdf"],
      ["Prime Model Portfolio", "/pdf/MO-IMP-Leaflet-Prime-Model.pdf"],
      ["Zodiac Model Portfolio", "/pdf/MO-IMP-Leaflet-Zodiac-Model.pdf"],
      ["MO Technical Focus Model Portfolio", "/pdf/MO-Technical-Focus-Model-Portfolio.pdf"],
      ["RS Alpha Bluechip Model Portfolio", "/pdf/MO-IMP-RS-Alpha-Bluechip-MP.pdf"],
      [
        "MO 20 Quant Momentum Model Portfolio",
        "/pdf/MO-IMP-MO-20-Quant-Momentum-Model-Portfolio-leaflet.pdf",
      ],
      ["APEX - Active NS Industry Champ Model Portfolio", "/pdf/MO_IMP-APEX.pdf"],
      ["ASPIRE - Active NS Mid & Small Cap Model Portfolio", "/pdf/MO_IMP-ASPIRE.pdf"],
      ["AN Alpha Pulse Model Portfolio", "/pdf/MO-IMP-AN-Alpha-Pulse-Model-Portfolio.pdf"],
      ["VS Bluechip Edge Model Portfolio", "/pdf/IMP-Bluechip-Edge-Model-Portfolio.pdf"],
      ["MO Combined Alpha Model Portfolio", "/pdf/1/Leaflet-Combined-Alpha-Model-Portfolio.pdf"],
      [
        "ASCEND - Active NS 5Tx5T Model Portfolio",
        "/pdf/IMP-ASCEND-Active-NS-5Tx5T-Model-Portfolio-Leaflet.pdf",
      ],
      ["MO Signia Model Portfolio", "/pdf/MO-IMP-Leaflet-Signia-Model-Portfolio.pdf"],
    ];

    return Object.fromEntries(entries.map(([title, pdf]) => [title, strip(pdf)]));
  };

  const getItemMeta = (item) => {
    const normalized = item.toLowerCase();
    if (
      normalized.includes("model portfolio") ||
      normalized.includes("imp product guide") ||
      normalized.includes("imp product brochure")
    ) {
      return {
        icon: "fa-book-open",
        detail:
          "IMP Info Hub — Motilal's Intelligent Model Portfolio leaflets and overview decks (official online hub).",
      };
    }
    if (normalized.includes("stratx")) {
      return {
        icon: "fa-robot",
        detail:
          "Rule-based automation for derivatives execution, monitoring, and strategy deployment on Motilal Oswal.",
      };
    }
    if (normalized.includes("equity")) {
      return {
        icon: "fa-chart-line",
        detail:
          "Market-linked equity participation with execution support and strategy alignment.",
      };
    }
    if (normalized.includes("futures") || normalized.includes("options")) {
      return {
        icon: "fa-wave-square",
        detail:
          "Derivative access for hedging, tactical positioning, and volatility-focused strategies.",
      };
    }
    if (normalized.includes("mutual fund") || normalized.includes("etf")) {
      return {
        icon: "fa-chart-pie",
        detail:
          "Diversified portfolio building using allocation-led investment frameworks.",
      };
    }
    if (normalized.includes("ipo")) {
      return {
        icon: "fa-building-columns",
        detail:
          "Primary market opportunities with research-backed issue participation.",
      };
    }
    if (normalized.includes("bond")) {
      return {
        icon: "fa-file-invoice-dollar",
        detail:
          "Debt exposure options for income stability and defensive allocation balance.",
      };
    }
    if (normalized.includes("aif")) {
      return {
        icon: "fa-layer-group",
        detail:
          "Alternative strategy exposure through category-based structures and manager-led mandates.",
      };
    }
    if (normalized.includes("unlisted") || normalized.includes("pre-ipo")) {
      return {
        icon: "fa-city",
        detail:
          "Off-market participation in unlisted and pre-listing opportunities through assisted execution.",
      };
    }
    if (normalized.includes("insurance")) {
      return {
        icon: "fa-shield-heart",
        detail:
          "Protection planning across life, health, and general insurance partners for risk balancing.",
      };
    }
    if (normalized.includes("fixed deposit") || normalized.includes("fixed income")) {
      return {
        icon: "fa-piggy-bank",
        detail:
          "Income-oriented allocation options for conservative and stability-focused portfolios.",
      };
    }
    if (normalized.includes("research") || normalized.includes("reports")) {
      return {
        icon: "fa-magnifying-glass-chart",
        detail:
          "Structured reports and insights supporting informed investment decision-making.",
      };
    }
    if (normalized.includes("wealth") || normalized.includes("private")) {
      return {
        icon: "fa-user-tie",
        detail:
          "High-touch portfolio planning for goal-focused and long-horizon wealth building.",
      };
    }
    if (normalized.includes("pms") || normalized.includes("amc")) {
      return {
        icon: "fa-briefcase",
        detail:
          "Professionally managed investment mandates with active strategy oversight.",
      };
    }
    if (normalized.includes("housing finance")) {
      return {
        icon: "fa-house-circle-check",
        detail:
          "Property-linked financing support through group housing finance capabilities.",
      };
    }
    if (normalized.includes("loan")) {
      return {
        icon: "fa-hand-holding-dollar",
        detail:
          "Liquidity options designed to unlock value without disturbing core investments.",
      };
    }
    return {
      icon: "fa-circle-check",
      detail:
        "Integrated into the broader advisory, execution, and monitoring service stack.",
    };
  };

  const productDetails = {
    "Equity delivery & intraday": {
      description:
        "Direct market participation for delivery and same-day strategies through Motilal's integrated trading stack.",
      features: [
        "Execution across NSE and BSE listed equities",
        "Delivery and intraday modes based on strategy need",
        "Integrated watchlists, order tools, and portfolio visibility",
        "Suitable for both active and long-term investors",
      ],
    },
    "Equity Futures & Options": {
      description:
        "Derivative access for tactical trades, hedging, and risk-managed market participation.",
      features: [
        "Index and stock derivatives coverage",
        "Supports directional and hedging strategies",
        "Research-backed setup and risk-aware participation",
        "Integrated with broader portfolio decision workflow",
      ],
    },
    "Commodity Futures & Options": {
      description:
        "Commodity derivatives participation for diversification and macro-linked tactical opportunities.",
      features: [
        "Exposure to commodity derivatives segments",
        "Useful for diversification and tactical positioning",
        "Research-assisted product understanding",
        "Integrated execution support",
      ],
    },
    "Currency Futures & Options": {
      description:
        "Currency derivative solutions for hedging FX exposure and taking tactical views on currency trends.",
      features: [
        "Major currency pairs access",
        "Hedging support for exposure-sensitive investors",
        "Tactical positioning for active strategies",
        "Aligned with risk and allocation frameworks",
      ],
    },
    "Global/USD products (GIFT route)": {
      description:
        "Global market exposure through USD-linked offerings routed via group GIFT capabilities.",
      features: [
        "Access to select global market opportunities",
        "USD product participation pathway",
        "Diversification beyond domestic-only allocation",
        "Integrated advisory support for suitability",
      ],
    },
    "Motilal STRATX (algo & automated F&O)": {
      description:
        "STRATX is Motilal Oswal’s platform for automated, rule-based derivatives trading—helping reduce emotional bias through predefined execution, monitoring, and performance views.",
      features: [
        "Automation for F&O strategies with disciplined execution",
        "Strategy discovery and deployment workflows",
        "Real-time monitoring, P&L, and strategy-wise analytics",
        "Designed for active traders comfortable with derivatives risk",
      ],
    },
    "AIF Navigator (Motilal Oswal alternate investments)": {
      description:
        "The AIF Navigator suite is Motilal Oswal’s structured access path to Category II and Category III alternate investment funds—spanning equity, credit, private markets, and real-estate-linked strategies with manager-led mandates.",
      features: [
        "Curated bouquet across Cat II and Cat III AIFs",
        "Open-ended and close-ended strategy options",
        "Higher ticket sizes versus retail mutual funds",
        "Onboarding, factsheets, and suitability support via advisory",
      ],
    },
    "Mutual Funds (equity, hybrid, debt)": {
      description:
        "Broad mutual fund platform spanning equity, debt, and hybrid solutions aligned with client goals.",
      features: [
        "Category-based allocations across risk profiles",
        "SIP/STP and long-horizon investing support",
        "Research-aligned fund selection process",
        "Portfolio review and rebalance guidance",
      ],
    },
    "Portfolio Management Services (PMS)": {
      description:
        "Professionally managed discretionary strategies across market-cap styles with long-term wealth focus.",
      features: [
        "Multiple PMS mandates by category and style",
        "Experienced fund managers and documented process",
        "Periodic strategy and portfolio disclosures",
        "Suitable for investors seeking managed mandates",
      ],
    },
    "Alternate Investment Funds (AIF Cat II & Cat III)": {
      description:
        "Alternative strategy exposure through Category II and III structures across equity and credit themes.",
      features: [
        "Open-ended and close-ended AIF opportunities",
        "Coverage across equity, private credit, and real estate themes",
        "Manager-specific strategy participation",
        "Higher minimum commitments relative to retail products",
      ],
    },
    "Unlisted & pre-IPO shares": {
      description:
        "Access to off-market unlisted and pre-listing opportunities through assisted discovery and execution.",
      features: [
        "Participation in unlisted growth-stage opportunities",
        "Off-market transfer and demat delivery process",
        "Assistance on availability and execution workflow",
        "Suitable for risk-aware long-horizon investors",
      ],
    },
    "Insurance (life, health, general)": {
      description:
        "Risk protection layer across life, health, and general insurance integrated with wealth planning.",
      features: [
        "Coverage from leading insurance providers",
        "Protection-oriented portfolio balancing",
        "Life, health, and general categories supported",
        "Advisory help for policy fitment and continuity",
      ],
    },
    "Category III AIFs (open/closed-ended strategies)": {
      description:
        "Category III alternatives focused on market-linked equity and tactical return-oriented strategies.",
      features: [
        "Includes open and close-ended strategy options",
        "Manager-led mandates across style preferences",
        "Performance and AUM-led comparison support",
        "Suitable for advanced portfolio diversification",
      ],
    },
    "Category II AIFs (private credit, PE, real estate)": {
      description:
        "Category II alternatives focused on private credit, buyout/private equity, and real estate opportunities.",
      features: [
        "Structured, tenure-based alternative allocations",
        "Themes include performing credit and private market assets",
        "Indicative IRR-led opportunity framing",
        "Typically suited to high-ticket, long-term investors",
      ],
    },
    "Motilal Oswal Founders Fund": {
      description:
        "A manager-led AIF strategy focused on quality businesses led by strong and aligned promoters.",
      features: [
        "Founder-quality and business-quality orientation",
        "Multi-cap strategy approach",
        "Aligned to long-term compounding outcomes",
        "Documented framework and periodic updates",
      ],
    },
    "Motilal Oswal Hedged Equity Multifactor": {
      description:
        "Factor-based equity approach with hedging overlays to manage drawdowns during adverse phases.",
      features: [
        "Growth and momentum factor orientation",
        "Tail-risk hedge construct",
        "Diversified basket-based portfolio design",
        "Built for risk-adjusted return intent",
      ],
    },
    "MO Alts India Credit Excellence Fund - I": {
      description:
        "Private credit-oriented Category II strategy targeting secured structures in mid-sized Indian opportunities.",
      features: [
        "Focus on secured and covenant-led structures",
        "Themes include growth, dislocated, and performing credit",
        "Downside-protection-led underwriting approach",
        "Tenure-based alternative allocation profile",
      ],
    },
    "MOAMC Founders Strategy": {
      description:
        "PMS strategy investing in businesses with strong promoter/management skin in the game and quality growth potential.",
      features: [
        "Capital preservation and capital appreciation focus",
        "Multi-cap style with active stock selection",
        "Research-led manager framework",
        "Built for long-term compounding approach",
      ],
    },
    "MOAMC Value Migration": {
      description:
        "PMS strategy focused on long-term compounding through quality businesses and migration opportunities.",
      features: [
        "Multi-cap positioning with active style execution",
        "High-conviction stock selection framework",
        "Designed for long-term allocation horizons",
        "Periodic manager communication and reviews",
      ],
    },
    "MOAMC Mid to Mega Strategy": {
      description:
        "PMS strategy centered on midcap growth and re-rating opportunities through bottom-up stock selection.",
      features: [
        "Mid and small-cap oriented opportunity set",
        "Bottom-up quality business identification",
        "Growth-plus-valuation balance",
        "Suitable for investors with higher risk tolerance",
      ],
    },
    "Renaissance India Next PMS": {
      description:
        "Flexi-cap PMS strategy aiming for superior risk-adjusted returns via diversified stock selection.",
      features: [
        "Cross market-cap diversified construct",
        "Top-down plus bottom-up blended process",
        "Designed for medium to long-term horizons",
        "Risk-adjusted return focus",
      ],
    },
    "Renaissance Opportunities Portfolio": {
      description:
        "Large-cap quality-oriented PMS strategy with disciplined portfolio construction and long-term orientation.",
      features: [
        "Large-cap dominant allocation profile",
        "Diversified 25-30 stock style approach",
        "Quality-growth blend",
        "Structured process and periodic review support",
      ],
    },
    "Corporate fixed deposits": {
      description:
        "Corporate FD offerings for investors seeking predictable accrual-oriented fixed income allocation.",
      features: [
        "Multiple issuers and tenures based on availability",
        "Rating-aware issuer selection support",
        "Suitable for conservative return expectations",
        "Works as stability bucket in overall allocation",
      ],
    },
    "Primary and secondary bonds": {
      description:
        "Bond offerings across primary and secondary opportunities for income and duration-oriented allocation.",
      features: [
        "Government and corporate debt instruments",
        "Yield-focused allocation options",
        "Primary and secondary availability pathways",
        "Useful for income and defensive diversification",
      ],
    },
    "Private credit and performing credit strategies": {
      description:
        "Alternative fixed-income strategies targeting risk-adjusted accrual through structured credit exposures.",
      features: [
        "Private credit and performing credit participation",
        "Security and covenant-oriented structures",
        "Multi-year holding orientation",
        "Higher-risk, higher-yield profile than vanilla debt",
      ],
    },
    "Sovereign Gold Bonds and floating-rate bonds": {
      description:
        "Fixed-income diversification through sovereign and rate-linked bond instruments for tactical allocation.",
      features: [
        "Sovereign-backed and floating-rate options",
        "Useful for inflation and rate-cycle positioning",
        "Portfolio diversification support",
        "Integrated into broader fixed-income bucket",
      ],
    },
    "Award-winning research and advisory support": {
      description:
        "Research-backed advisory framework combining house views, strategy notes, and actionable portfolio guidance.",
      features: [
        "Market, sector, and product-level insights",
        "Model portfolio and allocation guidance",
        "Periodic updates for decision support",
        "Advisory integrated with execution support",
      ],
    },
    "Private wealth management (HNI/UHNI)": {
      description:
        "High-touch wealth solutions designed for complex portfolios, advanced planning, and multi-asset allocation.",
      features: [
        "Dedicated relationship-led support model",
        "Customized portfolio and product mapping",
        "Multi-asset investment architecture",
        "Designed for HNI and UHNI client needs",
      ],
    },
    "Portfolio monitoring, review, and restructuring": {
      description:
        "Ongoing portfolio supervision to maintain alignment with goals, risk profile, and changing market context.",
      features: [
        "Periodic review and rebalance recommendations",
        "Goal-aligned restructuring support",
        "Risk and allocation drift monitoring",
        "Advisory continuity across market cycles",
      ],
    },
    "Loan Against Securities (LAS)": {
      description:
        "Liquidity support by borrowing against eligible financial holdings without liquidating core investments.",
      features: [
        "Leverage existing investment holdings for liquidity",
        "Useful for short-to-medium term funding needs",
        "Helps preserve long-term investment positions",
        "Advisory support on suitability and structure",
      ],
    },
    "Housing finance solutions": {
      description:
        "Home financing support through group housing finance capabilities for property-linked funding needs.",
      features: [
        "Housing loan-oriented product support",
        "Structured lending assistance through group channels",
        "Guidance on documentation and process",
        "Integrated with broader financial planning journey",
      ],
    },
    ...buildImpInfoHubProductDetails(),
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleSection = (index) => {
    setOpenSections((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  return (
    <div className="mo-wrapper">
      <Navbar />

      <section className="mo-hero">
        <div className="mo-hero-bg" />
        <div className="mo-container mo-hero-inner">
          <button className="mo-back-btn" onClick={() => navigate("/wealth")}>
            <i className="fas fa-arrow-left" /> Back to Wealth
          </button>

          <div className="mo-hero-grid">
            <div className="mo-hero-copy">
              <p className="mo-eyebrow">Detailed Partner View</p>
              <h1>{partner.name}</h1>
              <p className="mo-subtitle">{partner.subtitle}</p>
              <p className="mo-intro">{partner.intro}</p>

              <a
                className="mo-account-btn"
                href={MOTILAL_ACCOUNT_OPEN_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Create Motilal Oswal Account
                <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
              </a>

              <div className="mo-stat-row">
                <div className="mo-stat-card">
                  <span className="mo-stat-val">7+</span>
                  <span className="mo-stat-lbl">Core Offering Segments</span>
                </div>
                <div className="mo-stat-card">
                  <span className="mo-stat-val">450+</span>
                  <span className="mo-stat-lbl">Cities Covered</span>
                </div>
                <div className="mo-stat-card">
                  <span className="mo-stat-val">1.4 Cr+</span>
                  <span className="mo-stat-lbl">Registered Customers</span>
                </div>
                <div className="mo-stat-card">
                  <span className="mo-stat-val">35+</span>
                  <span className="mo-stat-lbl">Years of Experience</span>
                </div>
                <div className="mo-stat-card">
                  <span className="mo-stat-val">2,500+</span>
                  <span className="mo-stat-lbl">Business Locations</span>
                </div>
                <div className="mo-stat-card">
                  <span className="mo-stat-val">AIF + PMS</span>
                  <span className="mo-stat-lbl">Catalog Product Lines</span>
                </div>
              </div>
            </div>

            <div className="mo-hero-visual">
              <div className="mo-logo-wrap">
                <img src={partner.logo} alt={`${partner.name} logo`} className="mo-logo" />
              </div>
              <div className="mo-graph-card">
                <div className="mo-graph-header">Catalog Segment Strength</div>
                <div className="mo-bars">
                  {[92, 88, 79, 85, 72, 68].map((h, i) => (
                    <div key={i} className="mo-bar-col">
                      <div className="mo-bar" style={{ height: `${h}%` }} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mo-chip mo-chip-1">AIF Platform</div>
              <div className="mo-chip mo-chip-2">PMS Strategies</div>
              <div className="mo-chip mo-chip-3">Fixed Income</div>
            </div>
          </div>
        </div>
      </section>

      <section className="mo-details">
        <div className="mo-container">
          <div className="mo-detail-head">
            <h2>Complete Product Catalog</h2>
            <p>Expand each segment to view all product lines and capabilities.</p>
          </div>

          <div className="mo-accordion">
            {partner.sections.map((section, index) => {
              const isOpen = openSections.includes(index);
              return (
                <article className={`mo-acc-item ${isOpen ? "mo-open" : ""}`} key={section.heading}>
                  <button
                    className="mo-acc-trigger"
                    onClick={() => toggleSection(index)}
                    aria-expanded={isOpen}
                  >
                    <span>{section.heading}</span>
                    <i className={`fas ${isOpen ? "fa-minus" : "fa-plus"}`} />
                  </button>
                  {isOpen && (
                    <div className="mo-acc-body">
                      <div className="mo-point-grid">
                        {section.items.map((item) => {
                          const meta = getItemMeta(item);
                          return (
                            <article
                              className="mo-point-card"
                              key={`${section.heading}-${item}`}
                              onClick={() => setActiveProduct(item)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  setActiveProduct(item);
                                }
                              }}
                            >
                              <div className="mo-point-icon">
                                <i className={`fas ${meta.icon}`} />
                              </div>
                              <div className="mo-point-content">
                                <h4>{item}</h4>
                                <p>{meta.detail}</p>
                                <span className="mo-point-link">
                                  View details <i className="fas fa-arrow-up-right-from-square" />
                                </span>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mo-faq">
        <div className="mo-container">
          <div className="mo-detail-head">
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know before getting started.</p>
          </div>

          <div className="mo-faq-list">
            {FAQS.map((item, index) => {
              const isOpen = openFaq === index;
              return (
                <article className={`mo-faq-item ${isOpen ? "mo-open" : ""}`} key={item.q}>
                  <button
                    className="mo-faq-trigger"
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                  >
                    <span>{item.q}</span>
                    <i className={`fas ${isOpen ? "fa-minus" : "fa-plus"}`} />
                  </button>
                  {isOpen && (
                    <div className="mo-faq-body">
                      <p>{item.a}</p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {activeProduct && (
        <div className="mo-modal-overlay" onClick={() => setActiveProduct(null)}>
          <div className="mo-modal" onClick={(e) => e.stopPropagation()}>
            <button className="mo-modal-close" onClick={() => setActiveProduct(null)}>
              <i className="fas fa-xmark" />
            </button>
            <p className="mo-modal-eyebrow">Product Details</p>
            <h3>{activeProduct}</h3>
            <div className="mo-modal-block">
              <h4>Product Description</h4>
              <p>
                {productDetails[activeProduct]?.description ||
                  "Detailed product information is available through our advisory team and Motilal Oswal partner documentation."}
              </p>
            </div>
            <div className="mo-modal-block">
              <h4>Key Features</h4>
              <ul>
                {(productDetails[activeProduct]?.features || [
                  "Structured access through Motilal Oswal partner framework",
                  "Advisory-led suitability mapping",
                  "Integrated investment and monitoring support",
                ]).map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MotilalOswalPage;
